import { FUEL_TYPE_DATA, FUEL_TYPE_NDPS, LATERAL_VIEW_MEDIA_KEYS } from '../constants/carline-constants';
import { FIELD_NAME } from '../constants/form-constants';
import { formService } from '../form-service';
import {
	getEtiquetaDgtFromFuelType,
	getEtiquetasDgt,
	isComerciales,
	mapOkapiMediaAssets,
	normalizeFuelTypeText,
} from '../helpers/carline-helpers';
import { externalContentManager } from '../service/external-content';
import { Oferta } from '../types/oferta-types';
import { Carline, OkapiConsolidatedData, SalesgroupWithCarline } from '../types/vehicle-types';
import {
	normalizeHyphens,
	normalizeString,
	removeConsecutiveDuplicates,
	removeExtraSpaces,
} from '../utils/string-utils';

type CarlineWithTransformacion = Carline & {
	transformacion?: string;
};

class VehicleEntityClass {
	private static instance: VehicleEntityClass;

	private constructor() {}

	static getInstance(): VehicleEntityClass {
		if (!VehicleEntityClass.instance) {
			VehicleEntityClass.instance = new VehicleEntityClass();
		}
		return VehicleEntityClass.instance;
	}

	get carline(): Carline | undefined {
		return formService.getFieldValue(FIELD_NAME.CARLINE) as Carline;
	}

	get transformacion(): string | undefined {
		return (this.carline as CarlineWithTransformacion | undefined)?.transformacion;
	}

	get salesgroup(): SalesgroupWithCarline | undefined {
		return formService.getFieldValue(FIELD_NAME.SALESGROUP) as SalesgroupWithCarline;
	}

	get preconfiguration(): OkapiConsolidatedData | undefined {
		return formService.getFieldValue(FIELD_NAME.VEHICLE_PRECONFIGURATION);
	}

	get oferta(): Oferta | undefined {
		return formService.getFieldValue(FIELD_NAME.OFERTA);
	}

	get ofertaId(): number | undefined {
		return this.oferta?.id;
	}

	get carlineCode(): string | undefined {
		if (this.oferta?.carline?.code) {
			return String(this.oferta.carline.code);
		}

		if (this.preconfiguration?.model_code) {
			return this.preconfiguration.model_code?.split('_')[1];
		}

		if (this.salesgroup) {
			return String(this.salesgroup.carline.code);
		}

		if (!this.carline) return undefined;

		return String(this.carline.code);
	}

	get carlineTitle(): string | undefined {
		if (this.oferta?.carline?.title) {
			return this.normalizeStr(this.oferta.carline.title);
		}
		if (this.preconfiguration?.model_description) {
			return this.normalizeStr(this.preconfiguration.model_description);
		}

		if (this.salesgroup) {
			return this.normalizeStr(`${this.salesgroup.carline?.nuevo ? 'Nuevo ' : ''}${this.salesgroup.carline.title}`);
		}

		if (!this.carline) return undefined;

		return this.normalizeStr(`${this.carline.nuevo ? 'Nuevo ' : ''}${this.carline.title}`);
	}

	get salesgroupCode(): string | undefined {
		if (this.oferta?.carline?.salesgroup?.code) {
			return String(this.oferta.carline.salesgroup.code);
		}

		if (this.preconfiguration) {
			const salesgroup = this.getOption('SALESGROUP');
			return salesgroup?.code?.split(':')[1] ?? undefined;
		}

		if (this.salesgroup?.code) {
			return String(this.salesgroup.code);
		}

		return this.carline?.salesgroup?.code ? String(this.carline.salesgroup.code) : undefined;
	}

	get salesgroupTitle(): string | undefined {
		if (this.oferta?.carline?.salesgroup?.title) {
			return this.normalizeStr(this.oferta.carline.salesgroup.title);
		}

		if (this.preconfiguration) {
			const salesgroup = this.getOption('SALESGROUP');
			return this.normalizeStr(salesgroup?.description?.replace('SALESGROUP:', ''));
		}

		if (this.salesgroup?.title) {
			return this.normalizeStr(this.salesgroup.title);
		}

		return this.normalizeStr(this.carline?.salesgroup?.title);
	}

	get vehicleCode(): string | undefined {
		if (!this.carlineCode) {
			return undefined;
		}
		return isComerciales() ? `${this.carlineCode}_${this.salesgroupCode || '00000'}` : this.carlineCode;
	}

	get showroomUrl(): string | undefined {
		return this.carline?.showroomURL || undefined;
	}

	get lateralImage(): string | undefined {
		return (
			this.findLateralImage(this.oferta?.configuracion?.mediaAssets, 'oferta') ||
			this.findLateralImage(mapOkapiMediaAssets(this.preconfiguration?.meta?.mediaAssets), 'preconfiguracion') ||
			this.findLateralImage(this.salesgroup?.mediaAssets, 'salesgroup') ||
			this.findLateralImage(this.carline?.mediaAssets, 'carline') ||
			undefined
		);
	}

	get marketingCode(): string | undefined {
		return (
			formService.getFieldValue(FIELD_NAME.MARKETING_CODE) ||
			externalContentManager.marketingCode ||
			this.oferta?.configuracion.marketingCode ||
			undefined
		);
	}

	get fuelType(): string {
		if (this.oferta?.configuracion?.motorizacion?.combustible) {
			switch (normalizeString(this.oferta.configuracion.motorizacion.combustible)) {
				case normalizeString(FUEL_TYPE_DATA.GASOLINA):
				case normalizeString(FUEL_TYPE_DATA.MILD_HYBRID):
				case normalizeString(FUEL_TYPE_DATA.DIESEL):
					return FUEL_TYPE_NDPS.COMBUSTION;
				case normalizeString(FUEL_TYPE_DATA.HIBRIDO):
				case normalizeString(FUEL_TYPE_DATA.HIBRIDO_ENCHUFABLE):
					return FUEL_TYPE_NDPS.HYBRID;
				case normalizeString(FUEL_TYPE_DATA.ELECTRICO):
					return FUEL_TYPE_NDPS.ELECTRIC;
				default:
					return FUEL_TYPE_NDPS.UNDEFINED;
			}
		}

		if (this.preconfiguration) {
			const fuel = this.getModelFilter('FuelType');
			if (fuel?.text == 'H\u00edbrido enchufable') {
				return FUEL_TYPE_NDPS.HYBRID;
			} else if (fuel?.text == 'El\u00e9ctrico') {
				return FUEL_TYPE_NDPS.ELECTRIC;
			} else {
				return FUEL_TYPE_NDPS.COMBUSTION;
			}
		}

		return this.hasElectricName() ? FUEL_TYPE_NDPS.ELECTRIC : FUEL_TYPE_NDPS.UNDEFINED;
	}

	get trimlineName(): string | undefined {
		if (!this.preconfiguration) return undefined;
		const trimline = this.getModelFilter('EquipmentLine');
		return this.normalizeStr(trimline?.text);
	}

	get preconfigurationDescription(): string | undefined {
		if (!this.preconfiguration) return undefined;
		const type = this.getOption('TYPE');
		return this.normalizeStr(type?.description?.replace('TYPE:', ''));
	}

	get modelName(): string | undefined {
		return this.preconfigurationDescription;
	}

	get modelCode(): string | undefined {
		if (!this.preconfiguration) return undefined;
		const baseType = this.getOption('BASETYPE');
		return baseType?.code?.replace('BASETYPE:', '');
	}

	get modelYear(): string | undefined {
		if (!this.preconfiguration) return undefined;
		const modelYear = this.getOption('MODELYEAR');
		return modelYear?.code?.replace('MODELYEAR:', '');
	}

	get motorizacion(): string | undefined {
		return (
			formService.getFieldValue(FIELD_NAME.MOTORIZACION) ||
			this.oferta?.configuracion.motorizacion.name ||
			this.oferta?.carline.salesgroup?.model?.name?.replace(this.carline?.title ?? '', '') ||
			this.modelName?.replace(this.carline?.title ?? '', '') ||
			undefined
		);
	}

	get etiquetas(): string[] {
		if (this.oferta?.configuracion?.etiquetaDGT) {
			return getEtiquetasDgt([this.oferta.configuracion.etiquetaDGT]);
		}

		if (this.preconfiguration) {
			const fuel = this.getModelFilter('FuelType');
			const fuelText = normalizeFuelTypeText(fuel?.text ?? '');
			const etiqueta = getEtiquetaDgtFromFuelType(fuelText);
			return etiqueta ? [etiqueta] : [];
		}

		if (this.salesgroup) {
			return getEtiquetasDgt(this.salesgroup.etiquetasDGT ?? []);
		}

		return getEtiquetasDgt(this.carline?.etiquetasDGT ?? []);
	}

	// Devuelve la primera imagen lateral disponible segun el orden de prioridad de LATERAL_VIEW_MEDIA_KEYS.
	private findLateralImage(
	  mediaAssets?: Partial<Record<(typeof LATERAL_VIEW_MEDIA_KEYS)[number], string>>,
	  source?: string,
	 ): string | undefined {
	  if (!mediaAssets) return undefined;
	  if (source == 'carline' && this.salesgroup) return undefined; // no mostramos la imagen lateral del carline si estamos consultando un salesgroup
	  for (const key of LATERAL_VIEW_MEDIA_KEYS) {
	   if (mediaAssets[key]) {
		console.log('🚀 ~ VehicleEntity ~ findLateralImage', `Found: ${key} in ${source}`);
		return mediaAssets[key];
	   }
	  }
	  return undefined;
	 }


	private getOption = (category: string) => {
		return this.preconfiguration?.options?.find((option) => option.category == category);
	};

	private getModelFilter = (type: string) => {
		return this.preconfiguration?.meta?.modelFilter?.find((modelFilter) => modelFilter.type == type);
	};

	private normalizeStr = (description: string | undefined) => {
		if (!description) return undefined;
		return normalizeHyphens(removeExtraSpaces(removeConsecutiveDuplicates(description)));
	};

	private hasElectricName(): boolean {
		if (!this.carline?.title) return false;

		const carlineTitle = this.carline.title.toLowerCase().replace('nuevo', '').trim();

		if (isComerciales()) {
			if (carlineTitle.substring(0, 3) == 'id.' || carlineTitle.substring(0, 2) == 'e-') {
				return true;
			}
		} else if (carlineTitle.substring(0, 3) == 'id.') {
			return true;
		}
		return false;
	}
}

export const vehicleEntity = VehicleEntityClass.getInstance();
